import { useState, useEffect } from 'react';
import { useProjectStore } from '@/stores';
import { Modal, Button } from '@/components/ui';
import { adjustTaskChain, getDownstreamTasks, getDaysDifference } from '@/utils/taskChain';
import type { Task } from '@/types';
import { AlertTriangle, Link2, Calendar } from 'lucide-react';

interface TaskChainAdjustModalProps {
  isOpen: boolean;
  onClose: () => void;
  onParentClose?: () => void;
  task: Task | null;
  newEndDate: string;
  latestFormData?: { start: string; end: string };
}

export function TaskChainAdjustModal({ isOpen, onClose, onParentClose, task, newEndDate, latestFormData }: TaskChainAdjustModalProps) {
  const { tasks, updateTask } = useProjectStore();
  const [affectedTasks, setAffectedTasks] = useState<Task[]>([]);
  const [daysDiff, setDaysDiff] = useState(0);

  useEffect(() => {
    if (task && newEndDate) {
      const diff = getDaysDifference(task.end, newEndDate);
      setDaysDiff(diff);
      
      const downstream = getDownstreamTasks(task.id, tasks);
      setAffectedTasks(downstream);
    }
  }, [task, newEndDate, tasks]);

  const handleConfirm = () => {
    if (!task) return;

    // 使用最新表单数据中的 start 和 end
    const finalStart = latestFormData?.start || task.start;
    const finalEnd = latestFormData?.end || newEndDate;

    // 更新当前任务
    updateTask(task.id, { start: finalStart, end: finalEnd });

    // 调整后续任务
    const updates = adjustTaskChain(task, finalEnd, tasks);
    updates.forEach(updatedTask => {
      updateTask(updatedTask.id, {
        start: updatedTask.start,
        end: updatedTask.end,
      });
    });

    onClose();
    onParentClose?.();
  };

  const handleCancel = () => {
    // 使用最新表单数据中的 start 和 end
    const finalStart = latestFormData?.start || task?.start || '';
    const finalEnd = latestFormData?.end || task?.end || newEndDate;

    // 只更新当前任务，不调整后续任务
    if (task) {
      updateTask(task.id, { start: finalStart, end: finalEnd });
    }
    onClose();
    onParentClose?.();
  };

  if (!task) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="调整关联节点"
      size="md"
    >
      <div className="space-y-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
            <div>
              <h4 className="font-medium text-yellow-800">任务时间已修改</h4>
              <p className="text-sm text-yellow-600 mt-1">
                任务 "{task.name}" 的结束日期已更改，
                {daysDiff > 0 ? `延后 ${daysDiff} 天` : `提前 ${Math.abs(daysDiff)} 天`}
              </p>
            </div>
          </div>
        </div>

        {affectedTasks.length > 0 ? (
          <>
            <div className="flex items-center text-gray-700">
              <Link2 className="w-4 h-4 mr-2" />
              <span className="font-medium">发现 {affectedTasks.length} 个关联任务</span>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 max-h-[200px] overflow-y-auto">
              {affectedTasks.map((t, index) => (
                <div key={t.id} className="flex items-center py-2 border-b border-gray-200 last:border-0">
                  <span className="text-sm text-gray-500 w-6">{index + 1}.</span>
                  <span className="text-sm font-medium text-gray-800 flex-1">{t.name}</span>
                  <div className="flex items-center text-xs text-gray-500">
                    <Calendar className="w-3 h-3 mr-1" />
                    {t.start} ~ {t.end}
                  </div>
                </div>
              ))}
            </div>

            <p className="text-sm text-gray-600">
              是否自动调整这些关联任务的开始和结束日期？工期将保持不变。
            </p>
          </>
        ) : (
          <p className="text-sm text-gray-600">该任务没有后续关联任务。</p>
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <Button variant="ghost" onClick={handleCancel}>
            不调整
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={affectedTasks.length === 0}
          >
            确认调整
          </Button>
        </div>
      </div>
    </Modal>
  );
}
